import { Dictionary } from '../../utils/types'

import { ContactUsReason } from './mappers'

export type RegistrationNumber = {
  companyRegistrationTypeCode: string
  name: string
  languages: Dictionary<{
    title: string
    placeholder: string
  }>
}

export enum ConfigLoadingState {
  NotLoaded = 'NOT_LOADED',
  Loading = 'LOADING',
  Loaded = 'LOADED',
  Failed = 'FAILED'
}

export enum PrimaryIdentifier {
  Company = 'Company',
  Report = 'Report'
}

export enum SearchWidgetDisplayMode {
  WithFilters = 'WithFilters',
  WithoutFilters = 'WithoutFilters'
}

export enum PricesMode {
  OnlyGross = 'OnlyGross',
  WithTaxInfo = 'WithTaxInfo',
  DecideByContractType = 'DecideByContractType'
}

export interface ConfigState {
  status: ConfigLoadingState
  countryCode: string
  pageSizes: Dictionary<number>
  localeMap: Dictionary<LocaleMap>
  content?: ContentConfiguration
  registrationNumbers: Dictionary<RegistrationNumber[]>
  publicUrl: string
  completedReportStatus: string
  googleRecaptchaSiteKey: string
  viewHtmlIcon: boolean
  officialDocumentsCountries: string[]
  gtmId: string
  loadedLanguage?: string
  apiBlocked: boolean
  pricesMode: PricesMode
  primaryIdentifier: PrimaryIdentifier
  domesticOfferingCountries: string[]
  domesticOfferingFallbackCountry: string
  redirectToCartOnItemAdded: boolean
  orderProcessingTimeoutSec: number
  officeEnabled: boolean
  import: Dictionary<ImportConfig>
  maximumNumberOfSupportedItems: number
  localCountries: string[]
  showAiChatbotButton: boolean
  showAIColumsOnArchive: boolean
  showAvailableBalance: boolean
}

export type LocaleMap = {
  umbraco: string
  api: string
}

export type ImportConfig = {
  headers: Dictionary<string>
  businessIdTypes: Dictionary<string>
}

export type JsonConfig = {
  googleRecaptchaSiteKey: string
  viewHtmlIcon: boolean
  officialDocumentsCountries: string[]
  gtmId: string
  countryCode: string
  publicUrl: string
  pageSizes: Dictionary<number>
  localeMap: Dictionary<LocaleMap>
  registrationNumbers: Dictionary<RegistrationNumber[]>
  completedReportStatus?: string
  pricesMode: PricesMode
  primaryIdentifier?: PrimaryIdentifier
  domesticOfferingCountries: string[]
  redirectToCartOnItemAdded: boolean
  orderProcessingTimeoutSec: number
  officeEnabled: boolean
  import: Dictionary<ImportConfig>
  maximumNumberOfSupportedItems: number
  localCountries: string[]
}

export type SearchFilter = {
  label: string
  identifier: string
  placeholder?: string
  countries?: CountryFilter[]
}

export type CountryFilter = {
  label: string
  countryCode: string
}

export type Link = {
  url: string
  text: string
  target?: string
}

export type DropDownLink = {
  header: string
  links: Link[]
}

export type NavigationLink = {
  linkModel?: Link
  dropdownLinksMenuModel?: DropDownLink
}

export type UmbracoImage = {
  url: string
  alt?: string
}

export type Banner = {
  id: string
  title: string
  description: string
  link?: Link
  textColor: string
  image?: UmbracoImage
  backgroundColor?: string
  icon?: string
}

export type ContentConfiguration = {
  cmsHomeUrl: string
  cmsFaqUrl: string
  cmsLoginUrl: string
  cmsContactsUrl: string
  externalSearchLink?: Link
  searchWidgetDisplayMode: SearchWidgetDisplayMode
  searchFilters: SearchFilter[]
  countryFilterEnabled: boolean
  defaultCountryFilter?: CountryFilter
  otherCountriesFilter?: CountryFilter[]
  navigationLinks: NavigationLink[]
  footerLinks: Link[]
  footerText: string
  logoInverted?: UmbracoImage
  logo?: UmbracoImage
  mobileLogoInverted?: UmbracoImage
  mobileLogo?: UmbracoImage
  sessionTimeout?: number
  headerBanner?: Banner
  showBulkImportMenuInHeader?: boolean
  showBulkImportLinkAboveSearchButton?: boolean
  showAsBulkImportButton?: boolean
  contactWidget?: Banner
  showMulticolumnDropdown?: boolean
  showContactUsFormWidget?: boolean
  contactUsReason?: ContactUsReason
  preferredProductsEnabled: boolean | undefined
  showAiChatbotButton?: boolean
  sapAnonymousUserId: string | undefined
  showAIColumsOnArchive?: boolean
  selfRegistrationEnabled?: boolean
  showAvailableBalance?: boolean
}

export type Seo = {
  metaTitle: string
  pageTitle: string
  keywords: string[]
  metaDescription: string
}

export type OfficialDocuments = {
  asyncLoadingCountries: string[]
  infoBoxTitle?: string
  infoBoxDescription?: string
  infoBoxCtaTitle?: string
  infoBoxPromo?: string
}

export type CompanyDetailPageUmbracoConfig = {
  seo?: Seo
  banner?: Banner
  roleVisibility?: string[]
  officialDocuments?: OfficialDocuments
  canShowPriceForAnonymousUsers: boolean
}
